import { ReactNode } from 'react';
import { Decimal } from 'decimal.js';
import type { IDocumentExchangeRate } from '@insio/shared-types';
import { Card, CardContent } from '../../../../components/Card';
import { Icon } from '../../../../components/icons/Icon';
import { ErrorEntry } from '../../../../components/errors';
import { useLocale, useTranslation } from '../../../../providers/UIProvider';
import {
  useInvoiceAccountingCurrencyCode,
  useInvoiceCurrencyCode,
  useInvoiceIsVatDocument
} from '../../../../providers/InvoiceProvider';
import { cn, combineIssues, formatExchangeRate, severityStyle } from '../../../../utils';
import type { ValidationIssue } from '../../../../types';
import type { FormatterProps } from '../types/editor-config';
import { FormatterError } from './FormatterError';
import { useFormatMoney } from '../../../../hooks';
import { DEFAULT_PLACEHOLDER } from '@insio/format-utils';
import { ZERO } from '@insio/decimal';

/** Path → display-cell mapping for routing per-field validation issues. */
type CellKey =
  | 'totalAmountNoVat'
  | 'totalVatAmount'
  | 'totalAmountInclVat'
  | 'paymentStatus'
  | 'paidAmount'
  | 'accountingExchangeRate'
  | 'roundingAmount';

const KNOWN_PATHS: ReadonlySet<CellKey> = new Set([
  'totalAmountNoVat',
  'totalVatAmount',
  'totalAmountInclVat',
  'paymentStatus',
  'paidAmount',
  'accountingExchangeRate',
  'roundingAmount'
]);

interface GroupedIssues {
  byCell: Partial<Record<CellKey, ValidationIssue[]>>;
  topLevel: ValidationIssue[];
}

function groupIssues(errors: ValidationIssue[] | undefined): GroupedIssues {
  const byCell: Partial<Record<CellKey, ValidationIssue[]>> = {};
  const topLevel: ValidationIssue[] = [];
  for (const issue of errors ?? []) {
    const path = (issue.path ?? '') as CellKey | '';
    if (path === '') {
      topLevel.push(issue);
      continue;
    }
    if (KNOWN_PATHS.has(path)) {
      (byCell[path] ??= []).push(issue);
    } else {
      topLevel.push(issue);
    }
  }
  return { byCell, topLevel };
}

interface InvoiceTotalsValue {
  totalAmountNoVat?: Decimal | null;
  totalVatAmount?: Decimal | null;
  totalAmountInclVat?: Decimal | null;
  paymentStatus?: string | null;
  paidAmount?: Decimal | null;
  accountingExchangeRate?: IDocumentExchangeRate | null;
  roundingAmount?: Decimal | null;
  accountingAmount?: Decimal | null;
  accountingAmountNoVat?: Decimal | null;
  accountingTotalVatAmount?: Decimal | null;
}

function toDecimal(value: unknown): Decimal | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Decimal) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    try {
      const d = new Decimal(value);
      return d.isNaN() ? null : d;
    } catch {
      return null;
    }
  }
  return null;
}

const SECTION_HEADER_CLASS =
  'mb-sm text-xs font-semibold uppercase tracking-wide text-foreground-muted';

/**
 * Read-only display for an invoice's totals: a "Summary" panel (totals,
 * rounding, paid, remaining to pay) and, for a foreign-currency invoice, an
 * "accounting currency conversion" panel. Currencies come from the surrounding
 * `InvoiceProvider`.
 */
export function InvoiceTotalsFormatter({ value, errors }: FormatterProps): ReactNode {
  const t = useTranslation('modules.modals.editInvoiceTotals');
  const locale = useLocale();
  const invoiceCurrencyCode = useInvoiceCurrencyCode();
  const accountingCurrencyCode = useInvoiceAccountingCurrencyCode();
  const isVatDocument = useInvoiceIsVatDocument();

  const data = (value ?? {}) as InvoiceTotalsValue;
  const currency = invoiceCurrencyCode;

  // Same currency ⇒ no conversion: hide the accounting-conversion panel entirely.
  const currenciesSame =
    invoiceCurrencyCode != null &&
    accountingCurrencyCode != null &&
    invoiceCurrencyCode === accountingCurrencyCode;

  const formatMoney = useFormatMoney(currency);
  const formatAccountingMoney = useFormatMoney(accountingCurrencyCode);

  const totalNoVat = toDecimal(data.totalAmountNoVat);
  const totalInclVat = toDecimal(data.totalAmountInclVat);
  const paidAmount = toDecimal(data.paidAmount);
  const roundingAmount = toDecimal(data.roundingAmount);
  const rate = toDecimal(data.accountingExchangeRate?.rate);
  const vatAmount =
    toDecimal(data.totalVatAmount) ??
    (totalNoVat && totalInclVat ? totalInclVat.minus(totalNoVat) : null);
  const baseTotal = isVatDocument ? totalInclVat : totalNoVat;
  const remainingToPay = baseTotal ? baseTotal.minus(paidAmount ?? ZERO) : null;

  // Rate and amounts show for any foreign-currency invoice, so a missing one reads as empty.
  const showAccountingConversion =
    !!accountingCurrencyCode && accountingCurrencyCode !== invoiceCurrencyCode;
  // Stored amounts only — never derived from the rate here.
  const accNoVat = toDecimal(data.accountingAmountNoVat);
  const accVat = toDecimal(data.accountingTotalVatAmount);
  const accInclVat = toDecimal(data.accountingAmount);

  const { byCell, topLevel } = groupIssues(errors);

  const sectionCombined = combineIssues(topLevel);
  const sectionSeverity = severityStyle(sectionCombined?.severity);

  const formatRate = (v: Decimal) => formatExchangeRate(v, locale);

  const renderValueLine = (
    main: ReactNode,
    cellIssues: ValidationIssue[] | undefined
  ): ReactNode => (
    <span className='inline-flex flex-col gap-2xs items-end'>
      <span>{main}</span>
      <FormatterError issues={cellIssues} />
    </span>
  );

  const rateLine =
    rate != null && invoiceCurrencyCode && accountingCurrencyCode
      ? t('exchangeRateLine', {
          from: invoiceCurrencyCode,
          rate: formatRate(rate),
          to: accountingCurrencyCode
        })
      : rate != null
        ? formatRate(rate)
        : DEFAULT_PLACEHOLDER;

  const roundingDisplay =
    roundingAmount != null
      ? `${roundingAmount.gt(0) ? '+' : ''}${formatMoney(roundingAmount)}`
      : null;

  return (
    <div className='w-full space-y-md'>
      <section>
        <h3 className={SECTION_HEADER_CLASS}>{t('sectionTotals')}</h3>
        <Card className={cn('w-full bg-white border border-border-base', sectionSeverity)}>
          <CardContent className='p-sm space-y-sm'>
            {sectionCombined && sectionSeverity && (
              <div
                className={cn(
                  'flex items-start gap-xs rounded px-xs py-2xs',
                  sectionSeverity.bgColor
                )}
              >
                <Icon
                  name={sectionSeverity.icon}
                  className={cn('h-3.5 w-3.5 shrink-0 mt-2xs', sectionSeverity.iconColor)}
                  aria-hidden='true'
                />
                <span className={cn('text-xs', sectionSeverity.iconColor)}>
                  <ErrorEntry issue={sectionCombined} showActual />
                </span>
              </div>
            )}

            <table className='w-full'>
              <tbody>
                {isVatDocument && (
                  <>
                    <tr className='bg-white'>
                      <td className='py-2xs px-sm text-xs text-foreground-muted uppercase'>
                        {t('totalWithoutVat')}
                      </td>
                      <td className='py-2xs px-sm text-xs text-foreground-base text-right'>
                        {renderValueLine(formatMoney(totalNoVat), byCell.totalAmountNoVat)}
                      </td>
                    </tr>
                    <tr className='bg-background-muted'>
                      <td className='py-2xs px-sm text-xs text-foreground-muted uppercase'>
                        {t('totalVatAmount')}
                      </td>
                      <td className='py-2xs px-sm text-xs text-foreground-base text-right'>
                        {renderValueLine(formatMoney(vatAmount), byCell.totalVatAmount)}
                      </td>
                    </tr>
                  </>
                )}
                {roundingDisplay && (
                  <tr className='bg-white'>
                    <td className='py-2xs px-sm text-xs text-foreground-muted uppercase'>
                      {t('roundingAmount')}
                    </td>
                    <td className='py-2xs px-sm text-xs text-foreground-muted text-right'>
                      {renderValueLine(roundingDisplay, byCell.roundingAmount)}
                    </td>
                  </tr>
                )}
                <tr className='bg-background-muted border-t-2 border-border-base'>
                  <td className='py-2xs px-sm text-sm font-bold text-foreground-base uppercase'>
                    {t('totalAmount')}
                  </td>
                  <td className='py-2xs px-sm text-sm font-bold text-foreground-base text-right'>
                    {renderValueLine(
                      formatMoney(isVatDocument ? totalInclVat : totalNoVat),
                      isVatDocument ? byCell.totalAmountInclVat : byCell.totalAmountNoVat
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      <section>
        <h3 className={SECTION_HEADER_CLASS}>{t('sectionPayment')}</h3>
        <Card className='w-full bg-white border border-border-base'>
          <CardContent className='p-sm space-y-sm'>
            <table className='w-full'>
              <tbody>
                <tr className='bg-white'>
                  <td className='py-2xs px-sm text-xs text-foreground-muted uppercase'>
                    {t('paidAmount')}
                  </td>
                  <td className='py-2xs px-sm text-xs text-foreground-base text-right'>
                    {renderValueLine(
                      paidAmount ? (
                        <span className={cn('font-bold', paidAmount.gt(0) && 'text-success-text')}>
                          {formatMoney(paidAmount)}
                        </span>
                      ) : (
                        <span className='text-foreground-muted'>{DEFAULT_PLACEHOLDER}</span>
                      ),
                      byCell.paidAmount
                    )}
                  </td>
                </tr>
              </tbody>
            </table>

            <div className='flex items-center justify-between pt-sm border-t-2 border-border-base'>
              <span className='text-sm font-bold uppercase text-foreground-base'>
                {t('remainingToPay')}
              </span>
              <span className='text-base font-bold text-foreground-base'>
                {formatMoney(remainingToPay)}
              </span>
            </div>
          </CardContent>
        </Card>
      </section>

      {!currenciesSame && (
        <section>
          <h3 className={SECTION_HEADER_CLASS}>{t('accountingConversion')}</h3>
          <Card className='w-full bg-white border border-border-base'>
            <CardContent className='p-sm'>
              <table className='w-full'>
                <tbody>
                  <tr className='bg-white'>
                    <td className='py-2xs px-sm text-xs text-foreground-muted uppercase'>
                      {t('accountingCurrency')}
                    </td>
                    <td className='py-2xs px-sm text-xs text-foreground-base text-right'>
                      {accountingCurrencyCode ?? DEFAULT_PLACEHOLDER}
                    </td>
                  </tr>
                  {showAccountingConversion && (
                    <>
                      <tr className='bg-background-muted'>
                        <td className='py-2xs px-sm text-xs text-foreground-muted uppercase'>
                          <span className='inline-flex items-center gap-2xs'>
                            <Icon
                              name='triangle-exclamation'
                              className='h-3.5 w-3.5 shrink-0 text-foreground-muted'
                              aria-hidden='true'
                            />
                            {t('exchangeRate')}
                          </span>
                        </td>
                        <td className='py-2xs px-sm text-xs text-foreground-base text-right'>
                          {renderValueLine(rateLine, byCell.accountingExchangeRate)}
                        </td>
                      </tr>
                      {isVatDocument && (
                        <>
                          <tr className='bg-white'>
                            <td className='py-2xs px-sm text-xs text-foreground-muted uppercase'>
                              {t('withoutVat')}
                            </td>
                            <td className='py-2xs px-sm text-xs text-foreground-base text-right'>
                              {formatAccountingMoney(accNoVat)}
                            </td>
                          </tr>
                          <tr className='bg-background-muted'>
                            <td className='py-2xs px-sm text-xs text-foreground-muted uppercase'>
                              {t('vatAmount')}
                            </td>
                            <td className='py-2xs px-sm text-xs text-foreground-base text-right'>
                              {formatAccountingMoney(accVat)}
                            </td>
                          </tr>
                        </>
                      )}
                      <tr className='bg-background-muted border-t-2 border-border-base'>
                        <td className='py-2xs px-sm text-sm font-bold text-foreground-base uppercase'>
                          {t('total')}
                        </td>
                        <td className='py-2xs px-sm text-sm font-bold text-foreground-base text-right'>
                          {formatAccountingMoney(isVatDocument ? accInclVat : accNoVat)}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}

InvoiceTotalsFormatter.displayName = 'InvoiceTotalsFormatter';
